import { ReactText } from "react";
import { FormikHelpers } from "formik";
import { AxiosError } from "axios";

import { snackbarNotificationsService } from "../notifications/SnackbarNotificationsService";

export type FormFieldsValues = { [key: string]: any };
export interface ErrorContext {
  [key: string]: ReactText;
}

export interface ApiError {
  messageKey: string | undefined;
  context: ErrorContext | undefined;
  formActions: FormikHelpers<FormFieldsValues> | undefined;
  modalTitleKey: string | undefined;
  withForm(values: FormFieldsValues, actions: FormikHelpers<FormFieldsValues>): void;
  withContext(error: ErrorContext): void;
  withErrorMessage(message: string): void;
  withModalTitleKey(titleKey: string): void;
  showErrorInSnackbar(): void;
}

/**
 * A one-line description of the request that failed, used as the Error message.
 *
 * Without it the message is empty, and anything that stringifies a thrown API error —
 * the CRA dev overlay above all — has nothing to show but "[object Object]".
 */
const describeFailedRequest = (error?: AxiosError<any>): string => {
  const request = [error?.config?.method?.toUpperCase(), error?.config?.url]
    .filter(Boolean)
    .join(" ");
  const status = error?.response?.status;

  if (request && status) return `${request} failed with status ${status}`;
  if (request) return `${request} failed`;
  return error?.message || "API request failed";
};

/**
 * Extends Error on purpose. These objects are THROWN (see ApiService), and a thrown
 * non-Error carries no message and no stack: the browser cannot attach one, `unhandledrejection`
 * consumers get a bare object, and ApplicationError's declared `extends Error` is a lie the
 * runtime never honoured. Everything that reads `.message` off a caught API error — the dev
 * overlay, ErrorHandlerService.extractServerErrorMessage, InspectionReviewService.approve —
 * was reading `undefined` before this.
 */
export class BaseApiError extends Error implements ApiError {
  formFieldsValues: FormFieldsValues | undefined;
  formActions: FormikHelpers<FormFieldsValues> | undefined;
  context: ErrorContext | undefined;
  messageKey: string | undefined;
  modalTitleKey: string | undefined;

  constructor(error?: AxiosError<any>) {
    super(describeFailedRequest(error));
    // So the overlay/console read "NotFoundError: GET /media-files/12/review failed with
    // status 404" rather than a generic "Error".
    this.name = this.constructor?.name || "ApiError";
  }

  withForm = (values: FormFieldsValues, actions: FormikHelpers<FormFieldsValues>) => {
    this.formFieldsValues = values;
    this.formActions = actions;
  };

  withContext = (errorContext: ErrorContext) => {
    this.context = errorContext;
  };

  withErrorMessage = (messageKey: string) => {
    this.messageKey = messageKey;
  };

  showErrorInSnackbar = () => {
    snackbarNotificationsService.showError(this.messageKey ?? "default-error", this.context);
  };

  withModalTitleKey(titleKey: string): void {
    this.modalTitleKey = titleKey;
  }
}
